import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { ResultError, ResultSubtract } from "@/ImageRepo";
import ShepButton from "@/components/GenericComponents/ShepButton";
import {
  selectPendingAlert,
  setPendingAlert,
  selectPendingValidationAlert,
  setPendingValidationAlert,
} from "@/features/connections/artifactPreviewSlice";

const isError = (seg) => (seg.ValidateCode || "").trim().toUpperCase() === "E";

const linkStyle = () => ({
  color: "var(--status-error-intense)",
  textDecoration: "underline",
  cursor: "pointer",
  fontWeight: 600,
});

const SegmentLink = ({ seg, onNavigate }) => {
  if (!seg.ElementPosition) return <span>{seg.ItemCode}{seg.Description && ` - ${seg.Description}`}</span>;
  return (
    <span
      onClick={() => onNavigate(seg.ElementPosition)}
      style={linkStyle()}
    >
      {seg.ItemCode || seg.ElementPosition}
      {seg.Description && ` - ${seg.Description}`}
    </span>
  );
};

const SegmentSection = ({ title, items, onNavigate }) => {
  if (!items?.length) return null;
  return (
    <div style={{ marginBottom: "8px" }}>
      <strong>{title}</strong>
      <br />
      {items.map((seg, i) => (
        <span key={i}>
          <SegmentLink seg={seg} onNavigate={onNavigate} />
          <br />
        </span>
      ))}
    </div>
  );
};

const PreviewValidationAlert = ({ onNavigateToSegment }) => {
  const dispatch = useDispatch();
  const pendingAlert = useSelector(selectPendingAlert);
  const pendingValidationAlert = useSelector(selectPendingValidationAlert);
  const [open, setOpen] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const queueRef = useRef([]);

  const showNext = useCallback(() => {
    if (queueRef.current.length > 0) {
      setAlertData(queueRef.current.shift());
      setOpen(true);
    }
  }, []);

  // Main error alerts are pushed to the front of the queue
  useEffect(() => {
    if (pendingAlert == null) return;
    queueRef.current.unshift(pendingAlert);
    dispatch(setPendingAlert({ pendingAlert: null }));
    if (!open) showNext();
  }, [pendingAlert]);

  // Validation alerts are pushed to the back of the queue
  useEffect(() => {
    if (pendingValidationAlert == null) return;
    queueRef.current.push(pendingValidationAlert);
    dispatch(setPendingValidationAlert({ pendingValidationAlert: null }));
    if (!open) showNext();
  }, [pendingValidationAlert]);

  const handleClose = () => {
    setOpen(false);
    // Show the next queued alert after a brief delay so the dialog
    // close animation completes before the next one opens.
    setTimeout(() => {
      showNext();
    }, 150);
  };

  const handleNavigate = (segmentId) => {
    handleClose();
    onNavigateToSegment(segmentId);
  };

  if (!alertData) return null;

  const segments = alertData.segments || [];
  const hasErrors = segments.some((s) => isError(s));
  const status = alertData.status || (hasErrors ? "Error" : "Warning");
  const Icon = status === "Error" ? ResultError : ResultSubtract;

  const errors = segments.filter((s) => isError(s) && !s.IsLoop);
  const warnings = segments.filter((s) => !isError(s) && !s.IsLoop);
  const loopErrors = segments.filter((s) => isError(s) && s.IsLoop);
  const loopWarnings = segments.filter((s) => !isError(s) && s.IsLoop);
  const firstSegmentId = segments.find((s) => s.ElementPosition)?.ElementPosition ?? null;
  const hasSegments = errors.length > 0 || warnings.length > 0 || loopErrors.length > 0 || loopWarnings.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="results"
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { border: "0px" } }}
      sx={{ zIndex: 1900 }}
    >
      <div className="results-icon-container">
        <Icon className="results-status-icon" />
      </div>
      <div className="dialog" style={{ marginBottom: "0px" }}>
        <DialogTitle className="title" style={{ padding: "0px", marginBottom: "12px" }}>
          {alertData.title || status}
        </DialogTitle>
        <DialogContent className="dialog-content" style={{ padding: "0px", marginBottom: "32px" }}>
          <div className="results-message" style={{ margin: "0px" }}>
            {!hasSegments && alertData.message && (
              <div style={{ marginBottom: "8px" }}>{alertData.message}</div>
            )}
            <SegmentSection title="Error: blank element(s):" items={errors} onNavigate={handleNavigate} />
            <SegmentSection title="Warning: blank element(s):" items={warnings} onNavigate={handleNavigate} />
            <SegmentSection title="Error: loop(s) not iterated:" items={loopErrors} onNavigate={handleNavigate} />
            <SegmentSection title="Warning: loop(s) not iterated:" items={loopWarnings} onNavigate={handleNavigate} />
          </div>
        </DialogContent>
      </div>
      <DialogActions>
        <div className="link-btn-container" style={{ marginBottom: "28px" }}>
          <div className="results-multi-button">
            {firstSegmentId && (
              <ShepButton
                size="small"
                variant="secondary"
                className="second-button"
                onClick={() => handleNavigate(firstSegmentId)}
              >
                Go to First Segment
              </ShepButton>
            )}
            <ShepButton size="small" variant="primary" onClick={handleClose}>
              {alertData.buttonLabel || "Close"}
            </ShepButton>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewValidationAlert;
